import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components/Icon';
import { cn } from '~/lib/cn';
import { FlexRow } from '~/modules/artist-roster/components/flex/FlexRow';
import type { CampaignType } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/constants';

interface CampaignTypeRadioButtonProps {
  campaignType: CampaignType;
  isSelected: boolean;
}

export function CampaignTypeRadioButton({
  campaignType,
  isSelected,
}: CampaignTypeRadioButtonProps) {
  const { t } = useTranslation('campaign-planner', {
    keyPrefix: 'digitalCampaigns.planWizard.sections.typeOfCampaign',
  });

  return (
    <RadixRadioGroup.Item
      className={cn(
        'relative flex h-[194px] w-[297px] flex-col gap-md px-lg py-lg text-left',
        'rounded-sm border-px border-emphasis-subtle hover:border-primary-pressed',
        'bg-emphasis-default hover:bg-emphasis-strong',
        { 'opacity-stronger': !isSelected },
      )}
      value={campaignType}
      id={campaignType}
    >
      <FlexRow>
        <FlexRow
          className={cn(
            'h-[24px] items-center justify-center rounded-xs px-sm py-xs text-body-sm',
            'font-semibold leading-none bg-emphasis-default',
          )}
        >
          {t(`options.${campaignType}.category`)}
        </FlexRow>
        <div className="flex-grow" />
        <Icon
          type={isSelected ? 'circle-dot' : 'circle'}
          size="sm"
          variant="regular"
          className={isSelected ? 'text-primary-default' : 'text-strong'}
          aria-label={
            isSelected
              ? `${t(`options.${campaignType}.title`)} ${t('isSelected')}`
              : ''
          }
        />
      </FlexRow>

      <span className="text-body-sm font-bold leading-none">
        {t(`options.${campaignType}.title`)}
      </span>

      <p className="text-body-sm leading-tight">
        {t(`options.${campaignType}.description`)}
      </p>
    </RadixRadioGroup.Item>
  );
}

CampaignTypeRadioButton.displayName = 'CampaignTypeRadioButton';
